<template>
  <div
    class="reading-assessment base-form"
    @copy="event_cancellation"
    @paste="event_cancellation"
    @cut="event_cancellation"
  >
    <div
      class="statement-box mt-2"
      @copy="event_cancellation"
      @paste="event_cancellation"
      @cut="event_cancellation"
    >
      <h2>
        {{
          currentAssessmentQuestions[0]?.question_options?.statement_title || ""
        }}
      </h2>

      <h3 class="my-3">
        {{ currentAssessmentQuestions[0]?.question_options?.head_title || "" }}
      </h3>

      <div
        v-if="
          currentAssessmentQuestions[0]?.question_options?.date ||
          currentAssessmentQuestions[0]?.question_options?.to ||
          currentAssessmentQuestions[0]?.question_options?.from ||
          currentAssessmentQuestions[0]?.question_options?.Subject
        "
        class="sop-meta"
      >
        <p>
          <strong>Date:</strong>
          {{ currentAssessmentQuestions[0]?.question_options?.date || "" }}
        </p>
        <p>
          <strong>To:</strong>
          {{ currentAssessmentQuestions[0]?.question_options?.to || "" }}
        </p>
        <p>
          <strong>From:</strong>
          {{ currentAssessmentQuestions[0]?.question_options?.from || "" }}
        </p>
        <p>
          <strong>Subject:</strong>
          {{ currentAssessmentQuestions[0]?.question_options?.Subject || "" }}
        </p>
      </div>
      <p class="statement-text">
        {{ currentAssessmentQuestions[0]?.question_options?.statement || "" }}
      </p>
    </div>
    <div class="questions-box" v-if="currentAssessmentQuestions.length > 0">
      <h3>Questions</h3>
      <div
        class="question-item form-field"
        v-for="(question, index) in currentAssessmentQuestions"
        :key="index"
      >
        <label
          class="form-field-label"
          @copy="event_cancellation"
          @paste="event_cancellation"
          @cut="event_cancellation"
          >{{ index + 1 }}: {{ question.question_text }}
          <span class="required">*</span></label
        >
        <div class="form-field-input">
          <v-textarea
            :value="
              answers.find((a) => a.question_id === question.id)
                ?.question_answer || ''
            "
            auto-grow
            outlined
            :error="$v.answers.$each[index]?.$error"
            :error-messages="answerErrors(index)"
            placeholder="Type your answer here..."
            @input="onAnswerInput($event, question.id, index)"
            @paste="event_cancellation"
            @copy="event_cancellation"
            @cut="event_cancellation"
          ></v-textarea>
        </div>
      </div>
    </div>
    <v-row>
      <v-col cols="12">
        <div class="text-end">
          <v-btn
            style="max-width: 150px"
            :loading="submitLoading"
            @click="submitAnswers"
            color="primary"
            :disabled="disabled_next_question"
            class="next-btn"
            :class="{
              'base-submit-button': !disabled_next_question,
              'base-disable-button': disabled_next_question
            }"
          >
            {{ $t("shared.next") }}
          </v-btn>
        </div>
      </v-col>
    </v-row>
    <p v-if="submitError" class="error-text">{{ submitError }}</p>
  </div>
</template>

<script>
import { AppAssessments } from "@/interfaces/data_objects/app_assessments";
import { SUBMIT_APP_ASSESSMENT_ANSWERS } from "@/store/modules/candidates/constants";
import Vue from "vue";
import { required } from "vuelidate/lib/validators";
import { mapActions } from "vuex";

export default Vue.extend({
  name: "AppEngReadingQuestionsAssessment",
  props: {
    assessment_data: { type: Array, required: true },
    assessment_type: { type: String, required: false },
    restored_state: { type: Object, required: false, default: null }
  },
  data() {
    return {
      currentPart: 1,
      answers: [],
      answersMap: {},
      submitLoading: false,
      submitError: "",
      bot_speaking: false,
      time_over: false
    };
  },
  computed: {
    currentAssessmentQuestions() {
      return this.assessment_data?.filter(
        (a) =>
          a &&
          a.question_options &&
          a.question_options.part === this.currentPart
      );
    },
    isLastScenario() {
      return this.currentPart >= 2;
    },
    canSubmit() {
      if (!this.answers.length) return false;
      const allAnsweredCurrent = this.answers.every(
        (a) => a && String(a.question_answer || "").trim().length > 0
      );
      return (
        allAnsweredCurrent &&
        this.answers.length === this.currentAssessmentQuestions.length
      );
    },
    disabled_next_question() {
      if (this.submitLoading) return true;
      if (this.answers.length !== this.currentAssessmentQuestions.length)
        return true;
      const allAnswered = this.answers.every(
        (a) => a && String(a.question_answer || "").trim().length > 0
      );
      return !allAnswered;
    }
  },
  mounted() {
    // Restore from saved state if available
    if (this.restored_state) {
      this.restoreFromSavedState();
    } else {
      this.deriveCurrentIndexFromAnswers();
      this.prepareAnswersForCurrentScenario();
    }
  },
  watch: {
    restored_state: {
      handler(newVal) {
        if (newVal && (newVal.currentPart || newVal.answersMap)) {
          this.restoreFromSavedState();
        }
      },
      deep: true
    },
    assessment_data: {
      handler(newVal) {
        if (!Array.isArray(newVal) || newVal.length === 0) return;
        if (!this.restored_state) {
          this.deriveCurrentIndexFromAnswers();
          this.answersMap = {};
          this.prepareAnswersForCurrentScenario();
        }
      },
      deep: true
    },
    // Emit state changes for persistence
    answersMap: {
      handler() {
        this.emitStateChange();
      },
      deep: true
    },
    currentPart() {
      this.emitStateChange();
    }
  },
  methods: {
    ...mapActions("candidate", {
      submit_assessment_answers: SUBMIT_APP_ASSESSMENT_ANSWERS
    }),
    event_cancellation(event) {
      event.preventDefault();
    },
    /**
     * Restore state from saved session
     */
    restoreFromSavedState() {
      if (!this.restored_state) return;

      if (this.restored_state.currentPart) {
        this.currentPart = this.restored_state.currentPart;
      }
      if (this.restored_state.answersMap) {
        this.answersMap = { ...this.restored_state.answersMap };
        // Restore current answers from answersMap
        if (this.answersMap[this.currentPart]) {
          this.answers = [...this.answersMap[this.currentPart]];
        }
      }

      console.log("Reading section state restored");
      this.prepareAnswersForCurrentScenario();
    },
    /**
     * Emit current state for persistence
     */
    emitStateChange() {
      this.$emit("state_changed", {
        section: "reading",
        state: {
          currentPart: this.currentPart,
          answersMap: this.answersMap,
          answers: this.answers
        }
      });
    },
    prepareAnswersForCurrentScenario() {
      const questions = this.currentAssessmentQuestions;
      const existingForPart = this.answersMap[this.currentPart] || [];
      // Build answers from canonical question list, matching each answer by question_id
      // so every answer populates to its own question (fixes restore/resume)
      this.answers = questions.map((q) => {
        const found = existingForPart.find(
          (a) => a && Number(a.question_id) === Number(q.id)
        );
        return found
          ? { question_id: q.id, question_answer: found.question_answer || "" }
          : { question_id: q.id, question_answer: "" };
      });
      this.$set(this.answersMap, this.currentPart, this.answers);
      this.$v && this.$v.$reset();
    },
    answerErrors(index) {
      const vNode = this.$v?.answers?.$each[index]?.question_answer;
      if (!vNode || !vNode.$dirty) return [];
      const errs = [];
      if (!vNode.required) errs.push(this.$t("rules.required"));
      return errs;
    },
    buildScenarioAnswerPayload() {
      // Never send done: true from Reading – only the final Submit (Speaking)
      // should mark the assessment as submitted.
      const questions = this.currentAssessmentQuestions;
      if (!questions || !questions.length) return [];
      const questionPayloadArray = [];
      for (const question of questions) {
        questionPayloadArray.push({
          assessment_id: question.assessment_id,
          question_id: question.id,
          answer_type: AppAssessments.QuestionType.JSON,
          answer: this.answersMap[this.currentPart].find(
            (ans) => ans.question_id === question.id
          ).question_answer
        });
      }
      return questionPayloadArray;
    },
    deriveCurrentIndexFromAnswers() {
      // Determine first unanswered scenario based on backend-provided answers
      if (
        !Array.isArray(this.assessment_data) ||
        !this.assessment_data.length
      ) {
        this.currentPart = 1;
        return;
      }
    },
    async send_user_response() {
      // Called when timer expires - save all remaining scenarios as empty and complete assessment
      this.time_over = true;
      this.bot_speaking = true;
      await this.saveAllRemainingScenariosAsEmpty();
    },
    async saveAllRemainingScenariosAsEmpty() {
      // Save current scenario (whatever state it's in)
      this.answersMap[this.currentPart] = [...this.answers];

      // Fill remaining scenarios with empty answers
      let i = this.currentPart + 1;
      while (i < this.assessment_data.length) {
        const scenarioQuestions = this.assessment_data.filter(
          (a) => a?.question_options?.part === i
        );
        this.answersMap[i] = scenarioQuestions.map((q) => ({
          question_id: q.id,
          question_answer: ""
        }));
        i += 1;
      }

      // Save all scenarios one by one
      try {
        this.submitLoading = true;
        let idx = this.currentPart;
        while (idx < this.assessment_data.length) {
          const scenario = this.assessment_data[idx];

          const answerObject = {
            answers: this.answersMap[idx] || [],
            scenario_index: idx + 1,
            total_scenarios: this.assessment_data.length
          };

          const payload = {
            assessment_id: scenario.assessment_id,
            question_id: scenario.id,
            answer_type: AppAssessments.QuestionType.JSON,
            answer: JSON.stringify(answerObject)
          };

          const response = await this.submit_assessment_answers(payload);
          if (!response) {
            throw new Error("Failed to save scenario " + (idx + 1));
          }
          idx += 1;
        }

        // Emit completion after all saved
        this.$emit("assessment_completed", {
          answers: this.answersMap,
          total_scenarios: this.assessment_data.length,
          saved: true,
          time_expired: true
        });
      } catch (e) {
        this.submitError =
          (e && e.message) || this.$t("assessments.went-wrong").toString();
        this.bot_speaking = false;
      } finally {
        this.submitLoading = false;
      }
    },
    onAnswerInput(value, id, index) {
      const existing = this.answers.find((a) => a.question_id === id);

      if (!existing) {
        this.answers.push({
          question_id: id,
          question_answer: value
        });
      } else {
        existing.question_answer = value;
      }
      if (this.$v.answers.$each[index]?.question_answer) {
        this.$v.answers.$each[index].question_answer.$touch();
      }
    },
    submitAnswers() {
      this.$v.$touch();
      if (this.disabled_next_question) return;
      this.answersMap[this.currentPart] = [...this.answers];
      this.persistScenario();
    },
    async persistScenario() {
      this.submitError = "";
      const isLast = this.isLastScenario || this.time_over;
      const payloadArray = this.buildScenarioAnswerPayload();
      if (!payloadArray || !payloadArray.length) {
        this.submitError = this.$t("assessments.went-wrong").toString();
        return;
      }
      this.submitLoading = true;
      this.bot_speaking = true; // Pause timer during submission
      try {
        for (const payload of payloadArray) {
          const response = await this.submit_assessment_answers(payload);
          if (!response) {
            throw new Error("Empty response");
          }
        }

        this.$emit("scenario_completed", {
          index: this.currentPart,
          assessment_id: this.currentAssessmentQuestions[0]?.assessment_id,
          answers: this.answersMap[this.currentPart],
          saved: true
        });

        // Emit answered question ids for global progress tracking
        if (Array.isArray(this.currentAssessmentQuestions)) {
          const ids = this.currentAssessmentQuestions
            .map((q) => Number(q?.id))
            .filter((id) => Number.isFinite(id) && id > 0);
          if (ids.length > 0) {
            this.$emit("question_answered", ids);
          }
        }
        if (isLast) {
          this.$emit("assessment_completed", {
            answers: this.answersMap,
            total_scenarios: 2,
            saved: true
          });
        } else {
          this.currentPart += 1;
          this.prepareAnswersForCurrentScenario();
          this.bot_speaking = false; // Resume timer for next scenario
        }
      } catch (e) {
        this.submitError =
          (e && e.message) || this.$t("assessments.went-wrong").toString();
        this.bot_speaking = false; // Resume timer on error
      } finally {
        this.submitLoading = false;
      }
    }
  },
  validations() {
    return {
      answers: {
        $each: {
          question_answer: { required }
        }
      }
    };
  }
});
</script>
<style scoped>
.reading-assessment {
  padding: 20px;
}
.progress-bar {
  margin-bottom: 12px;
  font-weight: 600;
}
.statement-box {
  background: #f7f7f7;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 25px;
}
.statement-text {
  margin: 10px 0;
  line-height: 1.5;
}
.sop-meta p {
  margin: 4px 0;
}
.questions-box {
  margin-bottom: 16px;
}
.question-item {
  margin-bottom: 20px;
  .required {
    color: red;
  }
}
</style>
